<template>
  <div class="meal-wrap">
    <div class="card">
      <div class="row header">
        <h2>🍽️ Repas</h2>
        <button
          class="btn primary btn-meal"
          :disabled="busy"
          @click="addMeal"
          title="Enregistrer un repas maintenant"
        >
          Nourrir
        </button>
        <Confettis ref="confettis" />
      </div>

      <!-- Dernier repas -->
      <div class="block">
        <div class="label">Dernier repas</div>
        <div class="value">
          <div
            v-if="last"
            class="last-event last-event--clickable"
            role="button"
            tabindex="0"
            @click="openMealEditor(last)"
            @keydown.enter.prevent="openMealEditor(last)"
            @keydown.space.prevent="openMealEditor(last)"
          >
            <div class="last-date">{{ formatDateShort(last.ts) }}</div>
            <div v-if="lastMealKind" class="last-meal-kind">
              <span class="meal-emoji">{{ lastMealKind.emoji }}</span> {{ lastMealKind.label }}
            </div>
            <div class="last-time">{{ formatTime(last.ts) }}</div>
            <div class="last-detail">{{ agoText(last.ts) }}</div>
          </div>
          <span v-else class="muted">Aucun repas enregistré</span>
        </div>
      </div>

      <!-- Historique -->
      <div class="history-wrap">
        <button class="btn ghost" @click="toggleHistory" :disabled="busy">
          <span class="chev" :class="{ open: showHistory }">▾</span>
          Historique
        </button>

        <div v-show="showHistory" class="history-panel">
          <div v-if="mealsSorted.length === 0" class="muted">Aucune donnée.</div>

          <div v-for="group in mealsPage" :key="group.key" class="history-day">
            <div
              class="history-day-header"
              role="button"
              tabindex="0"
              @click="toggleHistoryDay(group.key)"
              @keydown.enter.prevent="toggleHistoryDay(group.key)"
              @keydown.space.prevent="toggleHistoryDay(group.key)"
            >
              <div class="history-day-title">
                <strong>{{ group.label }}</strong>
              </div>
            </div>

            <div
              v-for="m in group.meals"
              v-show="!isHistoryDayCollapsed(group.key)"
              :key="m.id"
              class="history-row"
              role="button"
              tabindex="0"
              @click="openMealEditor(m)"
              @keydown.enter.prevent="openMealEditor(m)"
              @keydown.space.prevent="openMealEditor(m)"
            >
              <div class="info">
                <div class="line">
                  <strong class="meal-time">{{ formatTime(m.ts) }}</strong>
                  <span class="meal-summary"><span class="meal-emoji">{{ m.mealEmoji }}</span> {{ m.mealLabel }}</span>
                </div>
              </div>
            </div>
          </div>

          <div v-if="mealsPageCount > 1" class="pagination">
            <button class="page-btn" :disabled="historyPage === 1" @click="goToPage(historyPage - 1)">‹</button>
            <span v-for="p in paginationPages" :key="typeof p === 'number' ? p : 'e' + p">
              <span v-if="p === '...'" class="page-ellipsis">…</span>
              <button v-else class="page-btn" :class="{ active: p === historyPage }" @click="goToPage(p)">{{ p }}</button>
            </span>
            <button class="page-btn" :disabled="historyPage === mealsPageCount" @click="goToPage(historyPage + 1)">›</button>
          </div>
        </div>
      </div>
      <!-- /Historique -->
    </div>

    <div v-if="editingMeal" class="edit-modal" @click.self="closeMealEditor">
      <form class="edit-dialog" @submit.prevent="saveMealEdit">
        <div class="edit-header">
          <h3>Modifier le repas</h3>
          <button type="button" class="edit-close" @click="closeMealEditor">
            x
          </button>
        </div>

        <label class="edit-field">
          <span>Heure</span>
          <input v-model="editForm.ts" type="datetime-local" required />
        </label>

        <div class="edit-actions">
          <button
            type="button"
            class="btn danger edit-delete"
            :disabled="savingEdit"
            @click="deleteMeal"
          >
            Supprimer
          </button>
          <button type="button" class="btn ghost" @click="closeMealEditor">
            Annuler
          </button>
          <button type="submit" class="btn primary" :disabled="savingEdit">
            {{ savingEdit ? "Enregistrement..." : "Enregistrer" }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import Confettis from "./Confettis.vue";
import axios from "axios";
import api from "../api/api";

const FOOD_EMOJIS = [
  "🥐", "🥯", "🥞", "🧇", "🥓", "🥚", "🍳", "🧀", "🥗", "🥙",
  "🥪", "🌮", "🌯", "🫔", "🥟", "🍜", "🍲", "🍛", "🍣", "🍱",
  "🍝", "🍕", "🍔", "🍟", "🌭", "🥘", "🫕", "🥫", "🍖", "🍗",
  "🥩", "🥡", "🍤", "🍙", "🍚", "🍘", "🍥", "🥠", "🥮", "🍢",
  "🍡", "🥧", "🍰", "🧁", "🍮", "🍩", "🍪", "🍫", "🍿", "🦴",
];

export default {
  name: "Meals",
  data() {
    return {
      busy: false,
      tick: 0,
      interval: null,
      last: null,
      meals: [],
      showHistory: false,
      historyLoaded: false,
      historyPage: 1,
      historyPageSize: 10,
      collapsedHistoryDays: {},
      editingMeal: null,
      editForm: {
        ts: "",
      },
      savingEdit: false,
    };
  },
  computed: {
    apiUrl() {
      return api.api;
    },
    mealsSorted() {
      const arr = (this.meals || []).slice();
      arr.sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime());
      return arr;
    },
    historyGroups() {
      const groups = [];
      const byKey = {};
      for (const meal of this.mealsSorted) {
        const key = this.localDateKey(meal.ts);
        if (!byKey[key]) {
          byKey[key] = {
            key,
            label: this.formatDateShort(meal.ts),
            meals: [],
          };
          groups.push(byKey[key]);
        }
        byKey[key].meals.push(meal);
      }
      for (const group of groups) {
        const kindsByMeal = {};
        group.meals
          .slice()
          .sort((a, b) => new Date(a.ts).getTime() - new Date(b.ts).getTime())
          .forEach((meal, index) => {
            kindsByMeal[this.mealKey(meal)] = this.mealKindForIndex(index, meal);
          });
        group.meals = group.meals.map(meal => {
          const kind = kindsByMeal[this.mealKey(meal)] || this.mealKindForIndex(2, meal);
          return {
            ...meal,
            mealEmoji: kind.emoji,
            mealLabel: kind.label,
          };
        });
      }
      return groups;
    },
    lastMealKind() {
      if (!this.last) return null;
      const lastKey = this.mealKey(this.last);
      for (const group of this.historyGroups) {
        const meal = group.meals.find(item => this.mealKey(item) === lastKey);
        if (meal) {
          return {
            emoji: meal.mealEmoji,
            label: meal.mealLabel,
          };
        }
      }
      return this.mealKindForIndex(2, this.last);
    },
    mealsPageCount() {
      return Math.max(1, Math.ceil(this.historyGroups.length / this.historyPageSize));
    },
    mealsPage() {
      const start = (this.historyPage - 1) * this.historyPageSize;
      return this.historyGroups.slice(start, start + this.historyPageSize);
    },
    paginationPages() {
      const total = this.mealsPageCount;
      const cur = this.historyPage;
      if (total <= 7) {
        return Array.from({ length: total }, (_, i) => i + 1);
      }
      const pages = [1];
      if (cur > 3) pages.push("...");
      for (let i = Math.max(2, cur - 1); i <= Math.min(total - 1, cur + 1); i++) {
        pages.push(i);
      }
      if (cur < total - 2) pages.push("...");
      pages.push(total);
      return pages;
    },
  },
  async created() {
    await this.refresh();
    window.addEventListener("keydown", this.onKeyDown);
    // met à jour le "il y a X" régulièrement
    this.interval = setInterval(() => (this.tick = (this.tick + 1) % 1e9), 15000);
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.onKeyDown);
    if (this.interval) clearInterval(this.interval);
  },
  methods: {
    // "il y a 12 min" ou "il y a 2h05"
    agoText(ts) {
      this.tick;
      const ms = Date.now() - new Date(ts).getTime();
      if (isNaN(ms)) return "";
      const totalMin = Math.floor(ms / 60000);
      if (totalMin < 1) return "à l’instant";
      if (totalMin < 60) return `il y a ${totalMin} min`;
      const h = Math.floor(totalMin / 60);
      const m = totalMin % 60;
      return `il y a ${h}h${String(m).padStart(2, "0")}`;
    },
    async refresh() {
      const [lastRes, allRes] = await Promise.all([
        axios.get(`${this.apiUrl}/meals/last`),
        axios.get(`${this.apiUrl}/meals`),
      ]);
      this.last = lastRes.data || null;
      this.meals = Array.isArray(allRes.data) ? allRes.data : [];
      if (this.historyPage > this.mealsPageCount) this.historyPage = this.mealsPageCount;
    },
    async addMeal() {
      try {
        this.busy = true;
        await axios.post(`${this.apiUrl}/meals/add`, {});
        await this.refresh();
        this.launchConfettis();
      } catch (e) {
        alert(e?.response?.data?.error || "Erreur d’enregistrement");
      } finally {
        this.busy = false;
      }
    },
    launchConfettis() {
      // emoji du repas qui vient d'être enregistré
      const emoji = this.lastMealKind ? this.lastMealKind.emoji : "🦴";
      if (this.$refs.confettis !== undefined)
        this.$refs.confettis.launchConfettis([emoji]);
    },
    async toggleHistory() {
      this.showHistory = !this.showHistory;
      if (this.showHistory && !this.historyLoaded) {
        // On a déjà tout dans refresh(); garde ce hook pour parité avec WalkTracker
        this.historyLoaded = true;
      }
    },
    toggleHistoryDay(key) {
      this.collapsedHistoryDays = {
        ...this.collapsedHistoryDays,
        [key]: !this.collapsedHistoryDays[key],
      };
    },
    isHistoryDayCollapsed(key) {
      return !!this.collapsedHistoryDays[key];
    },
    goToPage(page) {
      const total = this.mealsPageCount;
      const next = Math.min(total, Math.max(1, page));
      if (next === this.historyPage) return;
      this.historyPage = next;
    },
    toDatetimeLocal(ts) {
      if (!ts) return "";
      const d = new Date(ts);
      if (isNaN(d.getTime())) return "";
      const offsetMs = d.getTimezoneOffset() * 60000;
      return new Date(d.getTime() - offsetMs).toISOString().slice(0, 16);
    },
    datetimeLocalToISO(value) {
      if (!value) return null;
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d.toISOString();
    },
    openMealEditor(meal) {
      this.editingMeal = meal;
      this.editForm = {
        ts: this.toDatetimeLocal(meal.ts),
      };
    },
    closeMealEditor() {
      if (this.savingEdit) return;
      this.editingMeal = null;
    },
    async saveMealEdit() {
      if (!this.editingMeal) return;
      const tsISO = this.datetimeLocalToISO(this.editForm.ts);
      if (!tsISO) {
        alert("Heure du repas invalide");
        return;
      }
      try {
        this.savingEdit = true;
        await axios.put(`${this.apiUrl}/meals/${this.editingMeal.id}`, {
          ts: tsISO,
        });
        this.editingMeal = null;
        await this.refresh();
      } catch (e) {
        alert(e?.response?.data?.error || "Erreur lors de la modification");
      } finally {
        this.savingEdit = false;
      }
    },
    async deleteMeal() {
      if (!this.editingMeal) return;
      const meal = this.editingMeal;
      const ok = confirm(
        `Supprimer le repas du ${this.formatDateShort(meal.ts).toLowerCase()} à ${this.formatTime(meal.ts)} ?`
      );
      if (!ok) return;
      try {
        this.savingEdit = true;
        await axios.delete(`${this.apiUrl}/meals/${meal.id}`);
        this.editingMeal = null;
        await this.refresh();
      } catch (e) {
        alert(e?.response?.data?.error || "Erreur lors de la suppression");
      } finally {
        this.savingEdit = false;
      }
    },
    onKeyDown(e) {
      if (e.key === "Escape" && this.editingMeal) this.closeMealEditor();
    },
    mealKey(meal) {
      return meal.id || meal.ts;
    },
    mealKindForIndex(index, meal) {
      const labels = ["Déjeuner", "Dîner", "Snack"];
      return {
        emoji: this.randomFoodEmojiForMeal(meal),
        label: labels[index] || "Snack",
      };
    },
    randomFoodEmojiForMeal(meal) {
      const key = String(this.mealKey(meal) || "");
      let hash = 0;
      for (let i = 0; i < key.length; i++) {
        hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
      }
      // mélange des bits (finaliseur murmur3) : des ids consécutifs donnent
      // des emojis qui ont l'air aléatoires, mais toujours les mêmes pour un repas
      hash ^= hash >>> 16;
      hash = Math.imul(hash, 0x85ebca6b);
      hash ^= hash >>> 13;
      hash = Math.imul(hash, 0xc2b2ae35);
      hash = (hash ^ (hash >>> 16)) >>> 0;
      return FOOD_EMOJIS[hash % FOOD_EMOJIS.length];
    },
    localDateKey(ts) {
      const d = new Date(ts);
      if (isNaN(d.getTime())) return "unknown";
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    },
    formatDateShort(ts) {
      if (!ts) return "Date inconnue";
      const d = new Date(ts);
      if (isNaN(d.getTime())) return "Date inconnue";
      const label = d.toLocaleDateString("fr-FR", {
        weekday: "long",
        day: "2-digit",
        month: "long",
      });
      return label.charAt(0).toUpperCase() + label.slice(1);
    },
    formatTime(ts) {
      if (!ts) return "—";
      const d = new Date(ts);
      if (isNaN(d.getTime())) return "—";
      return d.toLocaleTimeString("fr-FR", {
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    formatTs(ts) {
      if (!ts) return "—";
      const d = new Date(ts);
      return d.toLocaleString("fr-FR", {
        weekday: "long",
        day: "2-digit",
        month: "long",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
  components: {
    Confettis,
  },
};
</script>

<style scoped>
.btn-meal {
    background: #a76638 !important;
}
.meal-wrap {
  max-width: 640px;
  margin: 16px auto;
  padding: 0 12px;
}

/* Réutilise la même grammaire visuelle que WalkTracker */
.card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 14px;
  box-shadow: 0 6px 20px rgba(2, 6, 23, 0.06);
  padding: 16px;
}
.row.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}
.btn {
  border: 0;
  border-radius: 10px;
  padding: 8px 12px;
  cursor: pointer;
  color: #fff;
  transition: transform 80ms ease, opacity 120ms ease, background 120ms ease;
}
.btn.primary {
  background: var(--btn-primary-bg);
}
.btn:hover {
  transform: translateY(-1px);
  opacity: 0.95;
}
.btn.ghost {
  background: transparent;
  color: var(--text);
  border: 1px solid var(--border);
}
.btn.ghost:hover {
  background: var(--surface-2);
}

.block {
  padding: 10px 0;
  border-top: 1px dashed var(--border);
}
.block:first-of-type {
  border-top: 0;
}
.label {
  font-size: 12px;
  color: var(--text-muted);
  margin-bottom: 4px;
}
.value {
  color: var(--text);
}
.last-event {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  text-align: center;
}
.last-event--clickable {
  margin: -6px -8px;
  padding: 6px 8px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 120ms ease, box-shadow 120ms ease;
}
.last-event--clickable:hover,
.last-event--clickable:focus-visible {
  background: var(--surface-2);
  box-shadow: inset 0 0 0 1px var(--border);
  outline: none;
}
.last-meal-kind {
  color: var(--text);
  font-size: 14px;
  font-weight: 800;
  line-height: 1.2;
}
.last-time {
  color: var(--text);
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
}
.last-date {
  color: var(--text-2);
  font-size: 13px;
  font-weight: 700;
}
.last-detail {
  color: var(--text-2);
  font-size: 13px;
  font-weight: 700;
}
.muted {
  color: var(--text-muted);
}

/* Historique — cohérent avec WalkTracker */
.history-wrap {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
}
.chev {
  margin-left: 6px;
  display: inline-block;
  transition: transform 0.15s ease;
  transform: rotate(-90deg);
}
.chev.open {
  transform: rotate(0deg);
}
.history-panel {
  margin-top: 8px;
  border: 1px solid var(--border-2);
  border-radius: 10px;
  background: var(--surface);
  padding: 0;
  overflow: hidden;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px dashed var(--border);
}
.page-btn {
  min-width: 30px;
  height: 30px;
  padding: 0 6px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--surface);
  color: var(--text-2);
  cursor: pointer;
  font-size: 13px;
  transition: background 120ms ease;
}
.page-btn:hover:not(:disabled) {
  background: var(--surface-hover);
}
.page-btn:disabled {
  opacity: 0.4;
  cursor: default;
}
.page-btn.active {
  background: var(--btn-primary-bg);
  color: #fff;
  border-color: #111827;
}
.page-ellipsis {
  color: var(--text-faint);
  font-size: 13px;
  padding: 0 2px;
  line-height: 30px;
}

.history-day {
  overflow: hidden;
  margin-bottom: 0;
  border-bottom: 2px solid var(--border-2);
  background: var(--surface);
}
.history-day:last-of-type {
  margin-bottom: 0;
  border-bottom: 0;
}
.history-day-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 11px;
  background: var(--surface-2);
  border-radius: 0;
  cursor: pointer;
  text-align: center;
  transition: background 120ms ease;
}
.history-day-header:hover,
.history-day-header:focus-visible {
  background: var(--surface-hover);
  outline: none;
}
.history-day-title {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
}
.history-day-title strong {
  color: var(--text);
  font-size: 13px;
}
.history-day-summary {
  color: var(--text-2);
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}
.history-row {
  padding: 8px 11px;
  border-top: 1px dashed var(--border-2);
  cursor: pointer;
  transition: background 120ms ease, box-shadow 120ms ease;
}
.history-row:hover,
.history-row:focus-visible {
  background: var(--surface-2);
  box-shadow: inset 3px 0 0 #a76638;
  outline: none;
}
.history-row:last-child {
  padding-bottom: 9px;
}
.info .line {
  display: grid;
  grid-template-columns: minmax(0, 1fr) max-content;
  align-items: center;
  gap: 8px;
  color: var(--text);
}
.meal-time {
  min-width: 0;
  font-size: 14px;
  line-height: 1.2;
  white-space: nowrap;
}
.meal-summary {
  color: var(--text-2);
  font-size: 13px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
}
.meal-emoji {
  font-size: 15px;
  line-height: 1;
  vertical-align: -1px;
}

@media (max-width: 460px) {
  .history-day-title {
    flex-wrap: wrap;
  }
  .info .line {
    gap: 6px;
  }
  .meal-time {
    line-height: 1.35;
  }
  .meal-summary {
    font-size: 12px;
  }
}

/* Modale d'édition — identique à WalkTracker */
.edit-modal {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.48);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 9998;
  overflow-y: auto;
}
.edit-dialog {
  width: min(420px, calc(100vw - 32px));
  max-height: calc(100dvh - 32px);
  overflow-x: hidden;
  overflow-y: auto;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.22);
  padding: 16px;
  box-sizing: border-box;
}
.edit-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}
.edit-header h3 {
  margin: 0;
  color: var(--text);
  font-size: 18px;
}
.edit-close {
  width: 32px;
  height: 32px;
  border: 1px solid var(--border);
  border-radius: 50%;
  background: var(--surface);
  color: var(--text-2);
  cursor: pointer;
}
.edit-field {
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-bottom: 10px;
  color: var(--text-2);
  font-size: 13px;
  min-width: 0;
}
.edit-field input {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 9px 10px;
  color: var(--text);
  font-size: 14px;
  -webkit-appearance: none;
  appearance: none;
}
.edit-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 6px;
}
.btn.danger {
  background: #b91c1c;
}
/* bouton de suppression calé à gauche, loin de « Enregistrer » */
.edit-delete {
  margin-right: auto;
}

@media (max-width: 460px) {
  .edit-modal {
    align-items: flex-start;
    padding: max(12px, env(safe-area-inset-top)) 12px
      max(12px, env(safe-area-inset-bottom));
  }
  .edit-dialog {
    width: 100%;
    max-height: calc(100dvh - 24px);
    padding: 14px;
  }
  .edit-header h3 {
    font-size: 17px;
  }
  .edit-field input {
    font-size: 16px;
    padding: 10px 8px;
  }
  .edit-actions {
    flex-wrap: wrap;
  }
}
</style>
